/*
  Order Context for Coq Magic
  Provides global state management for cart, checkout, and order status
*/

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
    createOrder,
    isSupabaseConfigured
} from './supabaseClient';
import secureStorage from './secureStorage';

// Create context
const OrderContext = createContext();

// Custom hook to use order context
export function useOrder() {
    const context = useContext(OrderContext);
    if (!context) {
        throw new Error('useOrder must be used within an OrderProvider');
    }
    return context;
}

// Order Provider component
export function OrderProvider({ children }) {
    // Cart state (persisted to localStorage)
    const [cart, setCart] = useState(() => {
        try {
            const saved = localStorage.getItem('coq_cart');
            return saved ? JSON.parse(saved) : [];
        } catch {
            return [];
        }
    });

    // Customer state (stored in secure storage for privacy)
    const [customer, setCustomer] = useState(() => {
        try {
            const saved = secureStorage.getItem('coq_customer');
            return saved || null;
        } catch {
            return null;
        }
    });

    // Checkout state
    const [checkoutStep, setCheckoutStep] = useState('cart'); // cart, phone, address, confirm, success
    const [selectedCity, setSelectedCity] = useState(null);
    const [deliveryAddress, setDeliveryAddress] = useState('');
    const [orderNotes, setOrderNotes] = useState('');
    const [lastOrder, setLastOrder] = useState(null);

    // Phone state (simple)
    const [phoneNumber, setPhoneNumber] = useState('');

    // Loading states
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    // Persist cart to localStorage
    useEffect(() => {
        localStorage.setItem('coq_cart', JSON.stringify(cart));
    }, [cart]);

    // Persist customer to secure storage
    useEffect(() => {
        if (customer) {
            secureStorage.setItem('coq_customer', customer);
        }
    }, [customer]);

    // Load delivery zones removed - using simple city selection now

    // Cart functions
    const addToCart = useCallback((item) => {
        setCart(prev => {
            const existing = prev.find(i => i.id === item.id);
            if (existing) {
                return prev.map(i =>
                    i.id === item.id
                        ? { ...i, qty: i.qty + (item.qty || 1) }
                        : i
                );
            }
            return [...prev, { ...item, qty: item.qty || 1 }];
        });
    }, []);

    const removeFromCart = useCallback((itemId) => {
        setCart(prev => prev.filter(i => i.id !== itemId));
    }, []);

    const updateQuantity = useCallback((itemId, qty) => {
        if (qty <= 0) {
            removeFromCart(itemId);
            return;
        }
        setCart(prev => prev.map(i =>
            i.id === itemId ? { ...i, qty } : i
        ));
    }, [removeFromCart]);

    const clearCart = useCallback(() => {
        setCart([]);
    }, []);

    // Calculate totals - ESTIMATE ONLY (final totals calculated server-side)
    const TAX_RATE = 0.1;
    const subtotal = cart.reduce((sum, item) => sum + (item.price * item.qty), 0);
    const tax = Math.round(subtotal * TAX_RATE); // Estimate - server will recalculate
    const deliveryFee = 0; // Free delivery
    const total = subtotal + tax + deliveryFee; // Estimate - server will validate
    const cartCount = cart.reduce((sum, item) => sum + item.qty, 0);

    // Minimum order validation (kept low)
    const minOrder = 50;
    const meetsMinimum = subtotal >= minOrder;



    // Place order (optional userId links order to logged-in account for order history)
    const placeOrder = useCallback(async (customerName, userId = null) => {
        const last = Number(localStorage.getItem('coq_last_order_submit') || '0');
        if (Date.now() - last < 15000) {
            setError('Veuillez patienter quelques secondes avant de repasser une commande');
            return null;
        }

        if (!meetsMinimum) {
            setError(`Commande minimum: ${minOrder} DH`);
            return null;
        }

        if (!selectedCity) {
            setError('Veuillez sélectionner une ville');
            return null;
        }

        if (!deliveryAddress.trim()) {
            setError('Veuillez entrer votre adresse de livraison');
            return null;
        }

        const safeName = String(customerName || '').trim();
        const safePhone = String(customer?.phone || phoneNumber || '').trim();
        const safeAddress = String(deliveryAddress || '').trim();
        const safeNotes = String(orderNotes || '').trim();

        if (safeName.length > 80) {
            setError('Nom trop long');
            return null;
        }
        if (safePhone.length > 30) {
            setError('Téléphone invalide');
            return null;
        }
        if (safeAddress.length > 240) {
            setError('Adresse trop longue');
            return null;
        }
        if (safeNotes.length > 600) {
            setError('Notes trop longues');
            return null;
        }

        setIsLoading(true);
        setError(null);

        try {
            const orderData = {
                customerName: safeName,
                customerPhone: safePhone,
                deliveryAddress: safeAddress,
                deliveryZone: selectedCity,
                items: cart.map(item => ({
                    id: item.id,
                    title: item.title,
                    price: item.price,
                    qty: item.qty
                })),
                subtotal: subtotal,
                tax: tax,
                deliveryFee: deliveryFee,
                total: total,
                paymentMethod: 'COD',
                notes: safeNotes,
                userId: userId || null
            };

            let order;

            if (isSupabaseConfigured()) {
                try {
                    // Add 10 second timeout to prevent hanging forever
                    const timeoutPromise = new Promise((_, reject) =>
                        setTimeout(() => reject(new Error('timeout')), 10000)
                    );

                    const result = await Promise.race([createOrder(orderData), timeoutPromise]);

                    if (!result.success) {
                        throw new Error(result.error);
                    }
                    order = result.order;
                } catch (supabaseError) {
                    throw new Error(
                        supabaseError?.message === 'timeout'
                            ? 'La commande prend trop de temps. Veuillez réessayer.'
                            : (supabaseError?.message || 'Erreur lors de la commande')
                    );
                }
            } else {
                // Fallback for local testing
                const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
                const random = Math.random().toString(36).substring(2, 6).toUpperCase();
                order = {
                    ...orderData,
                    id: `local-${Date.now()}`,
                    order_number: `COQ-${today}-${random}`,
                    status: 'pending',
                    created_at: new Date().toISOString()
                };

                // Save to localStorage for testing
                const orders = JSON.parse(localStorage.getItem('coq_orders') || '[]');
                orders.push(order);
                localStorage.setItem('coq_orders', JSON.stringify(orders));
            }

            setLastOrder(order);
            localStorage.setItem('coq_last_order_submit', String(Date.now()));
            clearCart();
            setCheckoutStep('success');
            setIsLoading(false);

            return order;
        } catch (err) {
            setError(err.message || 'Erreur lors de la commande');
            setIsLoading(false);
            return null;
        }
    }, [cart, customer, phoneNumber, selectedCity, deliveryAddress, orderNotes, subtotal, tax, total, meetsMinimum, minOrder, clearCart]);

    // Reset checkout
    const resetCheckout = useCallback(() => {
        setCheckoutStep('cart');
        setSelectedCity(null);
        setDeliveryAddress('');
        setOrderNotes('');
        setPhoneNumber('');
        setError(null);
    }, []);

    // Context value
    const value = {
        // Cart
        cart,
        cartCount,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,

        // Totals
        subtotal,
        deliveryFee,
        total,
        minOrder,
        meetsMinimum,

        // Customer
        customer,
        setCustomer,

        // Checkout flow
        checkoutStep,
        setCheckoutStep,
        selectedCity,
        setSelectedCity,
        deliveryAddress,
        setDeliveryAddress,
        orderNotes,
        setOrderNotes,

        // Phone
        phoneNumber,
        setPhoneNumber,

        // Order
        placeOrder,
        lastOrder,
        resetCheckout,

        // Loading/Error
        isLoading,
        error,
        setError
    };

    return (
        <OrderContext.Provider value={value}>
            {children}
        </OrderContext.Provider>
    );
}

export default OrderContext;
